import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { nsLoaderContext } from '../../../../../ns-services/contexts.js';

import { NSLoader } from '../../../../../ns-services/ns-loader-service.js';

import JobSchema from '../../../../../models/job-schema.js';

import NSJobComponent from '../../../job.js';
import '../../../../../ns-elements/ns-job-layout.js';

import Company from '../../../../../models/company.js';
import nodeCash from '../../../../../models/node-cash.js';
import { calculateTransferPrice } from './transfer-logic.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import definition from '@job-schemas/company/node-cash/transfer.json';
import { jobSchema } from '../../../job-schema.js';

const logger = createLogger('ns-transfer-node-cash-to-company');

@customElement(NSTransferNodeCashToCompany.is)
export default class NSTransferNodeCashToCompany extends NSJobComponent {
  static get is() {
    return 'ns-transfer-node-cash-to-company';
  }

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @state()
  private __company?: Company;

  @state()
  private __nodeCash?: nodeCash[];

  @state()
  private __transferPrice?: number;

  @state()
  private __error?: string;

  static styles = [
    ...super.styles,
    css`
      .error-message {
        font-weight: 700;
      }

      .amount-row {
        gap: var(--wa-space-s);
      }

      .amount-row > * {
        min-width: 0;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { transfer: { type: 'button', label: 'Transfer' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this.__loader) throw new Error('Loader service not found');

    if (!this._job) throw new Error('Could not fetch job');
    if (!this._job.inputValues || !this._job.inputValues.recipientCompanyId)
      throw new Error('Could not find recipient company ID in the job input values');

    this.__company = await this.loadInput('company', 'recipientCompanyId');

    const nodeCashReq = await this._db.query('nodeCash', {
      currentBalance: {
        $gt: 0,
      },
    });
    if (!nodeCashReq || nodeCashReq.results.length === 0) {
      throw new Error('Unable to find any nodeCash in the system');
    }
    this.__nodeCash = nodeCashReq.results as unknown as nodeCash[];
  }

  private async __calculateTransferPrice(e: CustomEvent) {
    if (!this.__nodeCash) throw new Error('Could not find system nodeCash balance');

    const path = e.composedPath();
    const [eventTarget] = path;
    const target = eventTarget as HTMLTextAreaElement;

    const { transferPrice, error } = calculateTransferPrice(Number(target.value), this.__nodeCash);
    this.__error = error ?? '';
    this.__transferPrice = transferPrice;
  }

  async _outcomeTransferHandler() {
    if (!this.__transferPrice) throw new Error('Please select an amount to transfer');
    logger.info('Not Implemented Yet');
  }

  render() {
    const totalBalance = this.__nodeCash?.reduce(
      (total: number, nsCash: nodeCash) => (total = total + nsCash.currentBalance),
      0,
    );
    return html`
      <ns-job-layout>
        <p slot="intro">You are about to transfer nodeCash to ${this.__company?.name}</p>
        <div class="job-panel">
          ${this.__error
            ? html`
                <p class="error-message error-text">${this.__error}</p>
              `
            : nothing}
          <div class="layout horizontal">
            <wa-input
              class="flex w-100"
              disabled
              id="company-balance"
              type="number"
              value="0"
              label="Company nodeCash balance"
            ></wa-input>
          </div>
          <div class="layout horizontal">
            <wa-input
              class="flex w-100"
              disabled
              id="nodestream-balance"
              type="number"
              .value=${totalBalance}
              label="nodeStream balance"
            ></wa-input>
          </div>
          <div class="layout horizontal amount-row">
            <div class="layout vertical flex">
              <wa-input
                label="Transfer amount"
                id="transfer-amount"
                type="number"
                @input="${(e: CustomEvent) => this.__calculateTransferPrice(e)}"
              ></wa-input>
            </div>
            <div class="layout vertical flex">
              <wa-input
                disabled
                label="Transfer price"
                id="transfer-price"
                type="number"
                .value=${this.__transferPrice}
              ></wa-input>
            </div>
          </div>
        </div>
      </ns-job-layout>
    `;
  }
}
